import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { ApiService } from "../../api";
import type { AdminGroup, AdminUser } from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { space } from "../../theme/space";

const { Text } = Typography;

type Props = { onChanged: () => void };

/**
 * Groups: approval pools and template visibility, which are the same table.
 *
 * The **Used by** column is the point. A group here is not a label — it is
 * what `WorkflowRole.pool` restricts a signature to, and what `Template.admins`
 * grants a template on. That second one is a CASCADE, so deleting a group used
 * to delete every template it administered, stages and published versions
 * included, from a button that said nothing about it. The column says what
 * would break; the server refuses the delete regardless.
 *
 * A directory-managed group shows its badge and cannot have its membership
 * edited here: that edit would survive exactly until the next sync, and
 * accepting it is worse than refusing it.
 */
const GroupsPanel = ({ onChanged }: Props) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AdminGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<AdminGroup | null>(null);
  const [creating, setCreating] = useState(false);
  const [membersOf, setMembersOf] = useState<AdminGroup | null>(null);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [people, setPeople] = useState<AdminUser[]>([]);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await ApiService.apiAccountsAdminGroupsList(search || undefined));
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not load the groups")));
    }
    setLoading(false);
  }, [search, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const openMembers = async (group: AdminGroup) => {
    setMembersOf(group);
    try {
      const [members, everyone] = await Promise.all([
        ApiService.apiAccountsAdminGroupMembers(group.id),
        ApiService.apiAccountsAdminUsersList({ is_active: "true" }),
      ]);
      setMemberIds(members.map((m) => m.id));
      setPeople(everyone);
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not load the members")));
    }
  };

  const saveMembers = async () => {
    try {
      await ApiService.apiAccountsAdminSetGroupMembers(membersOf!.id, {
        user_ids: memberIds,
      });
      setMembersOf(null);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not save the members")));
    }
  };

  const save = async () => {
    const values = await form.validateFields();
    try {
      if (creating) await ApiService.apiAccountsAdminGroupsCreate(values);
      else await ApiService.apiAccountsAdminGroupsPartialUpdate(editing!.id, values);
      setEditing(null);
      setCreating(false);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not save the group")));
    }
  };

  const remove = (group: AdminGroup) => {
    Modal.confirm({
      title: t("Delete the group {{name}}?", { name: group.name }),
      content: t("Its members keep their accounts; only the group goes."),
      okText: t("Delete"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await ApiService.apiAccountsAdminGroupsDelete(group.id);
          load();
          onChanged();
        } catch (e) {
          message.error(apiErrorMessage(e, t("Could not delete the group")));
        }
      },
    });
  };

  const usedBy = (group: AdminGroup) => {
    const used = group.used_by || {
      approval_slots: 0,
      template_admins: 0,
      template_users: 0,
    };
    const templates = used.template_admins + used.template_users;
    if (!used.approval_slots && !templates)
      return <Text type="secondary">{t("Nothing yet")}</Text>;
    return (
      <Space size={4} wrap>
        {used.approval_slots > 0 && (
          <Tag color="purple" style={{ marginInlineEnd: 0 }}>
            {used.approval_slots === 1
              ? t("1 approval slot")
              : t("{{count}} approval slots", { count: used.approval_slots })}
          </Tag>
        )}
        {templates > 0 && (
          <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
            {templates === 1
              ? t("1 process")
              : t("{{count}} processes", { count: templates })}
          </Tag>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: t("Group"),
      dataIndex: "name",
      render: (name: string, row: AdminGroup) => (
        <Space size={6}>
          <span style={{ fontWeight: 500 }}>{name}</span>
          {row.directory_linked && (
            <Tooltip
              title={t(
                "Its membership comes from the company directory and is replaced on every sync.",
              )}
            >
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {t("From the directory")}
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t("People"),
      dataIndex: "member_count",
      width: 100,
    },
    {
      title: t("Used by"),
      key: "used_by",
      render: (_: unknown, row: AdminGroup) => usedBy(row),
    },
    {
      title: "",
      key: "actions",
      width: 260,
      render: (_: unknown, row: AdminGroup) => {
        const used =
          row.used_by &&
          (row.used_by.approval_slots ||
            row.used_by.template_admins ||
            row.used_by.template_users);
        return (
          <Space size={4}>
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => openMembers(row)}
            >
              {t("Members")}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setEditing(row);
                setCreating(false);
                form.setFieldsValue({ name: row.name });
              }}
            >
              {t("Rename")}
            </Button>
            <Tooltip
              title={
                used
                  ? t("Something depends on this group, so it cannot be deleted.")
                  : undefined
              }
            >
              <Button size="small" danger disabled={!!used} onClick={() => remove(row)}>
                {t("Delete")}
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: space.snug,
          marginBottom: space.base,
          flexWrap: "wrap",
        }}
      >
        <Input.Search
          allowClear
          style={{ width: 280 }}
          placeholder={t("Search groups")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ flex: 1 }} />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setCreating(true);
            setEditing(null);
            form.setFieldsValue({ name: "" });
          }}
        >
          {t("New group")}
        </Button>
      </div>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns as any}
        pagination={{ pageSize: 25, showSizeChanger: false }}
      />

      <Modal
        open={editing !== null || creating}
        title={creating ? t("New group") : t("Rename group")}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={save}
        okText={t("Save")}
        cancelText={t("Cancel")}
      >
        <Form form={form} layout="vertical" style={{ marginTop: space.base }}>
          <Form.Item name="name" label={t("Name")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={membersOf !== null}
        title={t("Who is in {{name}}", { name: membersOf?.name ?? "" })}
        onCancel={() => setMembersOf(null)}
        onOk={saveMembers}
        okText={t("Save")}
        okButtonProps={{ disabled: membersOf?.directory_linked }}
        cancelText={t("Cancel")}
        width={560}
      >
        {membersOf?.directory_linked ? (
          <Text type="warning">
            {t(
              "This group's membership comes from the company directory. Change it there, or unlink the group on the Directory tab.",
            )}
          </Text>
        ) : (
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%", marginTop: space.base }}
            placeholder={t("Pick people")}
            value={memberIds}
            onChange={setMemberIds}
            options={people.map((p) => ({
              value: p.id,
              label: `${p.name} · ${p.email}`,
            }))}
          />
        )}
      </Modal>
    </div>
  );
};

export default GroupsPanel;
